// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * A small promise wrapper around the crypto worker. Each call gets an id so
 * concurrent encrypt/decrypt requests resolve to the right caller.
 */
import type { FileHeader } from './types.ts';

interface Pending {
  resolve: (v: unknown) => void;
  reject: (e: Error) => void;
}

export class CryptoClient {
  private worker: Worker;
  private seq = 0;
  private pending = new Map<number, Pending>();
  private ready: Promise<void>;

  constructor(pass: string, room: string) {
    this.worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });
    this.ready = new Promise<void>((resolve, reject) => {
      const onReady = (ev: MessageEvent) => {
        if (ev.data?.type === 'ready') {
          this.worker.removeEventListener('message', onReady);
          resolve();
        }
      };
      this.worker.addEventListener('message', onReady);
      this.worker.addEventListener('error', () => reject(new Error('crypto worker failed to start')));
    });
    this.worker.addEventListener('message', (ev) => this.onMessage(ev));
    this.worker.postMessage({ type: 'init', pass, room });
  }

  private onMessage(ev: MessageEvent): void {
    const d = ev.data;
    if (!d || typeof d.id !== 'number') return;
    const p = this.pending.get(d.id);
    if (!p) return;
    this.pending.delete(d.id);
    if (d.type === 'error') p.reject(new Error(d.message || 'crypto error'));
    else p.resolve(d);
  }

  whenReady(): Promise<void> {
    return this.ready;
  }

  /** Encrypt file bytes and return the ready-to-send frame. */
  async encrypt(path: string, mtime: number, size: number, buf: ArrayBuffer): Promise<Uint8Array> {
    await this.ready;
    const id = ++this.seq;
    const res = await new Promise<{ frame: ArrayBuffer }>((resolve, reject) => {
      this.pending.set(id, { resolve: resolve as (v: unknown) => void, reject });
      this.worker.postMessage({ type: 'encrypt', id, path, mtime, size, buf }, [buf]);
    });
    return new Uint8Array(res.frame);
  }

  /** Decrypt a received frame back into its header + plaintext bytes. */
  async decrypt(frame: ArrayBuffer): Promise<{ header: FileHeader; bytes: Uint8Array }> {
    await this.ready;
    const id = ++this.seq;
    const res = await new Promise<{ header: FileHeader; buf: ArrayBuffer }>((resolve, reject) => {
      this.pending.set(id, { resolve: resolve as (v: unknown) => void, reject });
      this.worker.postMessage({ type: 'decrypt', id, frame }, [frame]);
    });
    return { header: res.header, bytes: new Uint8Array(res.buf) };
  }

  destroy(): void {
    this.worker.terminate();
    for (const p of this.pending.values()) p.reject(new Error('crypto worker terminated'));
    this.pending.clear();
  }
}
